import React, { useRef, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { FONT, NAVY } from "../../Policy/OnlinePolicy/components/formStyles";

const MB = 1024 * 1024;
const sizeText = (bytes) => (bytes >= MB ? `${(bytes / MB).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

/**
 * The insurer's policy PDFs for RoadZen (it needs them with every certificate): several files, PDF only, 25 MB each,
 * at most 10 (RoadZen's limits). The app's other upload boxes take one file, so this one is new. Files kept on a draft
 * show as "On file" (`saved`); a new choice replaces them.
 */
const PdfFilesPicker = React.memo(function PdfFilesPicker({ files = [], onChange, saved = [], disabled = false, error = "", max = 10, maxMb = 25 }) {
  const inputRef = useRef(null);
  const [problem, setProblem] = useState("");
  const [over, setOver] = useState(false);

  const add = (picked) => {
    const list = Array.from(picked || []);
    const bad = list.find((f) => !/\.pdf$/i.test(f.name) && f.type !== "application/pdf");
    if (bad) return setProblem(`${bad.name} is not a PDF. Upload the insurer's policy PDF`);
    const big = list.find((f) => f.size > maxMb * MB);
    if (big) return setProblem(`${big.name} is over ${maxMb} MB`);
    const next = [...files, ...list];
    if (next.length > max) return setProblem(`Upload at most ${max} PDFs`);
    setProblem("");
    onChange(next);
    return undefined;
  };

  const shownError = problem || error;
  return (
    <Box>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Upload the insurer's policy PDF"
        onClick={() => !disabled && inputRef.current && inputRef.current.click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) inputRef.current && inputRef.current.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          if (!disabled) add(e.dataTransfer.files);
        }}
        sx={{
          border: `1.5px dashed ${shownError ? "#F04438" : over ? NAVY : "#C9D7E8"}`,
          borderRadius: "10px",
          backgroundColor: over ? "#EEF3F9" : "#F9FAFB",
          px: 2,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <CloudUploadOutlinedIcon sx={{ color: NAVY, fontSize: 30 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: NAVY }}>Click to upload or drop the policy PDF here</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#667085" }}>
            PDF only · up to {maxMb} MB each · at most {max} files
          </Typography>
        </Box>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          data-testid="roadzen-pdf-input"
          onChange={(e) => {
            add(e.target.files);
            e.target.value = "";
          }}
        />
      </Box>
      {shownError && (
        <Typography className="errorMessage" sx={{ mt: 0.75 }}>
          {shownError}
        </Typography>
      )}
      {(files.length > 0 || saved.length > 0) && (
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
          {files.length === 0 &&
            saved.map((f) => (
              <Box key={`saved-${f.index}`} sx={{ display: "flex", alignItems: "center", gap: 1, fontFamily: FONT, fontSize: 13 }}>
                <PictureAsPdfOutlinedIcon sx={{ color: "#B42318", fontSize: 20 }} />
                <span style={{ overflowWrap: "anywhere" }}>{f.fileName}</span>
                <span style={{ color: "#067647", fontSize: 12, fontWeight: 600 }}>On file</span>
              </Box>
            ))}
          {files.map((f, i) => (
            <Box key={`${f.name}-${i}`} sx={{ display: "flex", alignItems: "center", gap: 1, fontFamily: FONT, fontSize: 13 }}>
              <PictureAsPdfOutlinedIcon sx={{ color: "#B42318", fontSize: 20 }} />
              <span style={{ overflowWrap: "anywhere" }}>{f.name}</span>
              <span style={{ color: "#667085", fontSize: 12 }}>{sizeText(f.size)}</span>
              <IconButton size="small" aria-label={`Remove ${f.name}`} disabled={disabled} onClick={() => onChange(files.filter((_, j) => j !== i))}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
          {files.length > 0 && saved.length > 0 && (
            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#667085" }}>These replace the {saved.length} PDF(s) saved on the draft.</Typography>
          )}
          {files.length > 0 && (
            <Button size="small" sx={{ alignSelf: "flex-start", textTransform: "none", p: 0, minWidth: 0 }} disabled={disabled} onClick={() => onChange([])}>
              Remove all
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
});

export default PdfFilesPicker;
